import React from 'react';
import { Input, Button, Checkbox } from 'antd';
import { SearchOutlined } from '@ant-design/icons';

interface QueryParamsProps {
  companyFrom: string;
  companyTo: string;
  yearMonFrom: string;
  yearMonTo: string;
  deptFrom: string;
  deptTo: string;
  salesFrom: string;
  salesTo: string;
  checkDept: boolean;
  checkCompany: boolean;
  loading: boolean;
  onCompanyFromChange: (v: string) => void;
  onCompanyToChange: (v: string) => void;
  onYearMonFromChange: (v: string) => void;
  onYearMonToChange: (v: string) => void;
  onDeptFromChange: (v: string) => void;
  onDeptToChange: (v: string) => void;
  onSalesFromChange: (v: string) => void;
  onSalesToChange: (v: string) => void;
  onCheckDeptChange: (v: boolean) => void;
  onCheckCompanyChange: (v: boolean) => void;
  onQuery: () => void;
}

const QueryParams: React.FC<QueryParamsProps> = ({
  companyFrom, companyTo, yearMonFrom, yearMonTo, deptFrom, deptTo,
  salesFrom, salesTo, checkDept, checkCompany, loading,
  onCompanyFromChange, onCompanyToChange, onYearMonFromChange, onYearMonToChange,
  onDeptFromChange, onDeptToChange, onSalesFromChange, onSalesToChange,
  onCheckDeptChange, onCheckCompanyChange, onQuery,
}) => {
  return (
    <div className="bg-gray-50 border border-gray-200 rounded p-4 mb-4">
      {/* Row 1: Company range */}
      <div className="flex flex-wrap items-center gap-4 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium" style={{ width: 96 }}>公司代號[從]</span>
          <Input
            value={companyFrom}
            onChange={(e) => onCompanyFromChange(e.target.value.toUpperCase())}
            placeholder="起"
            style={{ width: 120 }}
            maxLength={12}
          />
          <span className="text-sm">[至]</span>
          <Input
            value={companyTo}
            onChange={(e) => onCompanyToChange(e.target.value.toUpperCase())}
            placeholder="迄"
            style={{ width: 120 }}
            maxLength={12}
          />
        </div>
      </div>

      {/* Row 2: Year-month range */}
      <div className="flex flex-wrap items-center gap-4 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium" style={{ width: 96 }}>年度月份[從]</span>
          <Input
            value={yearMonFrom}
            onChange={(e) => onYearMonFromChange(formatYearMonthInput(e.target.value))}
            placeholder="YYYY/MM"
            style={{ width: 120 }}
            maxLength={7}
          />
          <span className="text-sm">[至]</span>
          <Input
            value={yearMonTo}
            onChange={(e) => onYearMonToChange(formatYearMonthInput(e.target.value))}
            placeholder="YYYY/MM"
            style={{ width: 120 }}
            maxLength={7}
          />
        </div>
      </div>

      {/* Row 3: Department range */}
      <div className="flex flex-wrap items-center gap-4 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium" style={{ width: 96 }}>部門代號[從]</span>
          <Input
            value={deptFrom}
            onChange={(e) => onDeptFromChange(e.target.value.toUpperCase())}
            placeholder="起"
            style={{ width: 80 }}
            maxLength={4}
          />
          <span className="text-sm">[至]</span>
          <Input
            value={deptTo}
            onChange={(e) => onDeptToChange(e.target.value.toUpperCase())}
            placeholder="迄"
            style={{ width: 80 }}
            maxLength={4}
          />
        </div>
      </div>

      {/* Row 4: Sales range */}
      <div className="flex flex-wrap items-center gap-4 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium" style={{ width: 96 }}>業務代號[從]</span>
          <Input
            value={salesFrom}
            onChange={(e) => onSalesFromChange(e.target.value.toUpperCase())}
            placeholder="起"
            style={{ width: 120 }}
            maxLength={8}
          />
          <span className="text-sm">[至]</span>
          <Input
            value={salesTo}
            onChange={(e) => onSalesToChange(e.target.value.toUpperCase())}
            placeholder="迄"
            style={{ width: 120 }}
            maxLength={8}
          />
        </div>
      </div>

      {/* Row 5: Report type checkboxes */}
      <div className="flex flex-wrap items-center gap-4 mb-3">
        <span className="text-sm font-medium" style={{ width: 96 }}>印表選擇</span>
        <Checkbox checked={checkDept} onChange={(e) => onCheckDeptChange(e.target.checked)}>
          部門公司別
        </Checkbox>
        <Checkbox checked={checkCompany} onChange={(e) => onCheckCompanyChange(e.target.checked)}>
          公司部門別
        </Checkbox>
      </div>

      {/* Query button */}
      <div className="flex gap-2">
        <Button
          type="primary"
          icon={<SearchOutlined />}
          onClick={onQuery}
          loading={loading}
          disabled={!checkDept && !checkCompany}
        >
          查詢
        </Button>
      </div>
    </div>
  );
};

/**
 * Allow only digits and slash, auto-insert slash after 4 digits.
 */
function formatYearMonthInput(raw: string): string {
  const digits = raw.replace(/[^0-9]/g, '');
  if (digits.length <= 4) return digits;
  return digits.substring(0, 4) + '/' + digits.substring(4, 6);
}

export default QueryParams;
